/**
 * PressPdfBorders.jsx — the "Remove carton borders" switch that sits with the
 * Download press-ready PDF button, and the per-carton report that comes back.
 *
 * Removing a border is what a designer does in CorelDRAW with right-click None
 * on every panel: the outline goes, the fill stays, every other line stays. The
 * backend only does it when it can PROVE the shapes are the carton's pieces
 * (they tile it exactly) and that nothing else changed (a before/after render);
 * otherwise it plates the carton as drawn and says why. That "why" is the
 * report, and a carton that kept its borders when the operator asked for them
 * gone is shown first, not buried.
 *
 * Both the Layout screen and the Layout Editor render these, over ONE setting
 * held in App — so the choice made on one screen is the one the other exports.
 *
 * Contract 1.11 adds the printer's carton MARKS and BLEED beside it
 * (PressSheetSwitches / PressReportList): marks in line with every fold and
 * edge, skipped where they would land on a neighbour unless the operator says
 * print them; bleed from each carton's own artwork, withheld beside artwork
 * that is not the carton's (a job table in the notch), and every carton's own
 * artwork winning inside its cut line.
 */

import React from 'react';
import { useTheme } from './theme';
import { Pill } from './primitives';

export function RemoveBordersSwitch({ value, onChange, disabled = false }) {
  const { P, FN } = useTheme();
  return (
    <label
      title={'Switch off the outline of each carton panel and flap (the fill stays), the same as '
        + 'right-click None in CorelDRAW. Lines inside the panels are kept. The DIELINE layer is '
        + 'left out of the print. A carton whose outlines cannot be proven to be its panels is '
        + 'plated as drawn, and the report says why.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, cursor: disabled ? 'default' : 'pointer',
        fontFamily: FN, fontSize: 12, color: P.ink, opacity: disabled ? 0.5 : 1, userSelect: 'none',
      }}>
      <input
        type="checkbox"
        role="switch"
        aria-checked={value}
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: P.accent, margin: 0 }}
      />
      Remove carton borders
    </label>
  );
}

function counts(r) {
  if (r.status === 'removed') {
    return `${r.removed} removed${r.kept != null ? ` · ${r.kept} other line${r.kept === 1 ? '' : 's'} kept` : ''}`;
  }
  return null;
}

/**
 * @param {Object} props
 * @param {Object|null} props.report  borderReport(manifest), or null
 * @param {boolean} props.requested   whether removal was asked for this export
 */
export function BorderReportList({ report, requested }) {
  const { P, FN, MO } = useTheme();
  if (!report) {
    if (!requested) return null;
    return (
      <div style={{ fontFamily: FN, fontSize: 11, color: P.dim, lineHeight: 1.45 }}>
        No border report came back with the PDF — the backend may predate border removal, so
        check the cartons on the sheet.
      </div>
    );
  }
  // Cartons that kept their borders against the operator's wish, first.
  const rows = [...report.rows].sort((a, b) => Number(b.attention) - Number(a.attention));
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', borderRadius: 8,
      background: report.attention ? P.warnSoft : P.surface,
      border: `0.5px solid ${report.attention ? P.warnLine : P.rule}`,
    }}>
      <div style={{ fontFamily: MO, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: P.muted }}>
        Carton borders{report.attention ? ` · ${report.attention} left in` : ''}
      </div>
      {rows.map((r) => (
        <div key={r.sourceId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{r.sourceId}</span>
            <Pill tone={r.tone}>{r.label}</Pill>
            {counts(r) && <span style={{ fontFamily: FN, fontSize: 11, color: P.muted }}>{counts(r)}</span>}
            {r.dieline > 0 && (
              <span style={{ fontFamily: FN, fontSize: 11, color: P.muted }}>· DIELINE layer left out</span>
            )}
          </div>
          {r.attention && r.reason && (
            <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, lineHeight: 1.4 }}>{r.reason}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function Switch({ label, title, value, onChange, disabled }) {
  const { P, FN } = useTheme();
  return (
    <label title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, cursor: disabled ? 'default' : 'pointer',
      fontFamily: FN, fontSize: 12, color: P.ink, opacity: disabled ? 0.5 : 1, userSelect: 'none',
    }}>
      <input type="checkbox" role="switch" aria-checked={value} checked={value} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)} style={{ accentColor: P.accent, margin: 0 }} />
      {label}
    </label>
  );
}

/**
 * The printer's marks and bleed, for the next download.
 *
 * @param {Object} props
 * @param {boolean} props.marks, props.bleed
 * @param {'skip'|'print'} props.marksOnNeighbours
 */
export function PressSheetSwitches({
  marks, onMarks, marksOnNeighbours, onMarksOnNeighbours, bleed, onBleed,
  autoBleed = true, onAutoBleed = null, disabled = false,
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <Switch label="Marks" value={marks} onChange={onMarks} disabled={disabled}
        title={'The printer\'s marks: a short registration line in line with every fold and outer edge '
          + 'of each PRINTED carton, just outside its printed edge — never round the flaps prepress '
          + 'adds. Cartons whose folds are not known get marks for their outline only, and the '
          + 'report says which.'} />
      {marks && (
        <Switch label="Marks over neighbours" value={marksOnNeighbours === 'print'}
          onChange={(on) => onMarksOnNeighbours(on ? 'print' : 'skip')} disabled={disabled}
          title={'Off (the default): a mark that would land on another carton\'s printed area is left '
            + 'out and counted (flaps are free space for marks). On: it is printed anyway — only if the '
            + 'printer agrees.'} />
      )}
      <Switch label="Bleed" value={bleed} onChange={onBleed} disabled={disabled}
        title={'The printer\'s bleed past each carton\'s PRINTED edge — onto its own flaps too — where '
          + 'the artwork runs past it. It may run over a neighbour; each carton is then re-stamped '
          + 'inside its own cut line, so its own artwork always wins there. Artwork beside the carton '
          + 'that is not its own (a job details table, dimension arrows) never bleeds.'} />
      {bleed && onAutoBleed && (
        <Switch label="Auto bleed" value={autoBleed} onChange={onAutoBleed} disabled={disabled}
          title={'Where the artwork stops at a straight edge, stretch the edge\'s outermost pixels '
            + 'outward across the bleed (from the copy with its borders off, in its own CMYK). Off: '
            + 'only bleed the artwork itself carries is printed.'} />
      )}
    </span>
  );
}

/**
 * What came back about marks and bleed for the last download.
 *
 * @param {Object} props
 * @param {Object|null} props.report  pressReport(manifest), or null
 * @param {boolean} props.marks, props.bleed  what was asked for
 */
export function PressReportList({ report, marks, bleed }) {
  const { P, FN, MO } = useTheme();
  if (!report) {
    if (!marks && !bleed) return null;
    return (
      <div style={{ fontFamily: FN, fontSize: 11, color: P.dim, lineHeight: 1.45 }}>
        No marks or bleed report came back with the PDF — the backend may predate them, so
        check the sheet.
      </div>
    );
  }
  const m = report.marks;
  const b = report.bleed;
  const rows = b ? [...b.rows].sort((x, y) => Number(y.attention) - Number(x.attention)) : [];
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', borderRadius: 8,
      background: report.attention ? P.warnSoft : P.surface,
      border: `0.5px solid ${report.attention ? P.warnLine : P.rule}`,
    }}>
      <div style={{ fontFamily: MO, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: P.muted }}>
        Marks and bleed{report.attention ? ` · ${report.attention} to check` : ''}
      </div>
      {m && m.enabled && (
        <div style={{ fontFamily: FN, fontSize: 11, color: P.ink, lineHeight: 1.45 }}>
          {m.drawn} mark{m.drawn === 1 ? '' : 's'} drawn
          {m.skipped > 0 && ` · ${m.skipped} left out (they would land on a neighbour)`}
          {m.onNeighbour > 0 && ` · ${m.onNeighbour} printed over a neighbour`}
          {m.offSheet > 0 && ` · ${m.offSheet} off the sheet`}
          {m.outlineOnly.length > 0 && (
            <span style={{ color: P.muted }}>{` · outline only (folds not known): ${m.outlineOnly.join(', ')}`}</span>
          )}
        </div>
      )}
      {m && m.enabled && m.rows.map((r) => (
        <div key={`m-${r.sourceId}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{r.sourceId}</span>
          <span style={{ fontFamily: FN, fontSize: 11, color: P.muted }}>
            {`${r.cartons} carton${r.cartons === 1 ? '' : 's'} · ${r.drawn} marks`}
            {r.skipped > 0 ? ` · ${r.skipped} left out` : ''}
            {r.onNeighbour > 0 ? ` · ${r.onNeighbour} over a neighbour` : ''}
            {r.folds === 'known' ? ' · at folds and edges' : ' · outline only'}
          </span>
        </div>
      ))}
      {m && m.unresolved.length > 0 && (
        <div style={{ fontFamily: FN, fontSize: 11, color: P.warn, lineHeight: 1.4 }}>
          {`No marks for ${m.unresolved.map((u) => u.source_id).join(', ')}: its cut outline was not sent.`}
        </div>
      )}
      {b && rows.map((r) => (
        <div key={`b-${r.sourceId}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>{r.sourceId}</span>
            <Pill tone={r.tone}>{r.label}</Pill>
            {(r.artworkMm > 0 || r.generatedMm > 0 || r.skippedMm > 0 || r.withheldMm > 0) && (
              <span style={{ fontFamily: FN, fontSize: 11, color: P.muted }}>
                {[r.artworkMm > 0 && `${r.artworkMm} mm from the artwork`,
                  r.generatedMm > 0 && `${r.generatedMm} mm stretched`,
                  r.skippedMm > 0 && `${r.skippedMm} mm not stretched`,
                  r.withheldMm > 0 && `${r.withheldMm} mm withheld`].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          {r.attention && r.reason && (
            <div style={{ fontFamily: FN, fontSize: 11, color: P.muted, lineHeight: 1.4 }}>{r.reason}</div>
          )}
        </div>
      ))}
    </div>
  );
}
